import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiMail, FiLock, FiArrowRight, FiZap } = FiIcons;

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function AuthModal() {
  const { isAuthModalOpen, setIsAuthModalOpen, setIsPurchaseModalOpen, selection, signInWithGoogle } = useAppContext();
  const [tab, setTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isAuthModalOpen) return null;

  const close = () => { setIsAuthModalOpen(false); setError(''); setSuccess(''); };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    setLoading(false);
    setIsAuthModalOpen(false);
    if (selection) setIsPurchaseModalOpen(true);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    setSuccess('Account created! You can now sign in.');
    setTab('signin');
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
      <div className="relative w-full max-w-[400px] animate-slide-up">
        {/* Glow border */}
        <div className="absolute -inset-[1.5px] rounded-2xl" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff,#ff00cc)' }} />

        <div className="relative rounded-2xl overflow-hidden" style={{ background: '#09000f' }}>
          {/* Close */}
          <button onClick={close} className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 hover:text-white transition-all flex items-center justify-center">
            <FiX />
          </button>

          {/* Header */}
          <div className="px-8 pt-8 pb-6 text-center">
            {/* Logo mark */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', boxShadow: '0 0 30px rgba(255,0,200,0.5)' }}>
              <FiZap className="text-white text-2xl" />
            </div>

            <h2 className="font-black uppercase mb-1" style={{ color: '#fff', fontFamily: 'Impact,sans-serif', letterSpacing: '3px', fontSize: '26px' }}>
              {tab === 'signin' ? 'Welcome Back' : 'Claim Your Pixels'}
            </h2>
            <p className="text-sm" style={{ color: '#6b7280' }}>
              {tab === 'signin'
                ? 'Sign in to manage your pixel blocks'
                : 'Create an account — it takes 10 seconds'}
            </p>
          </div>

          <div className="px-8 pb-8 space-y-3">
            {/* Google */}
            <button
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
              style={{ background: '#fff', color: '#1a1a1a' }}
            >
              {googleLoading
                ? <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin" />
                : <GoogleIcon />}
              <span>{googleLoading ? 'Redirecting...' : 'Continue with Google'}</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
              <span className="text-[11px] uppercase tracking-widest" style={{ color: '#374151' }}>or</span>
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
            </div>

            {/* Tab toggle */}
            <div className="flex rounded-xl overflow-hidden p-1" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={() => { setTab('signin'); setError(''); setSuccess(''); }}
                className="flex-1 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all"
                style={tab === 'signin' ? { background: 'rgba(255,0,200,0.2)', color: '#ff00cc' } : { color: '#4b5563' }}
              >
                Sign In
              </button>
              <button
                onClick={() => { setTab('signup'); setError(''); setSuccess(''); }}
                className="flex-1 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all"
                style={tab === 'signup' ? { background: 'rgba(255,0,200,0.2)', color: '#ff00cc' } : { color: '#4b5563' }}
              >
                Sign Up
              </button>
            </div>

            {/* Alerts */}
            {success && (
              <div className="p-3 rounded-xl text-green-400 text-xs font-medium" style={{ background: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.2)' }}>
                {success}
              </div>
            )}
            {error && (
              <div className="p-3 rounded-xl text-red-400 text-xs font-medium" style={{ background: 'rgba(255,50,50,0.07)', border: '1px solid rgba(255,50,50,0.2)' }}>
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={tab === 'signin' ? handleSignIn : handleSignUp} className="space-y-3">
              {/* Email */}
              <div className="relative">
                <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 text-sm pointer-events-none" />
                <input
                  required type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-white text-sm focus:outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(255,0,200,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                />
              </div>

              {/* Password */}
              <div className="relative">
                <FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 text-sm pointer-events-none" />
                <input
                  required type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-white text-sm focus:outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(255,0,200,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-black text-sm uppercase tracking-widest text-white transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', boxShadow: '0 0 20px rgba(255,0,200,0.4)', fontFamily: 'Impact,sans-serif', letterSpacing: '2px' }}
              >
                {loading
                  ? <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  : <>{tab === 'signin' ? 'Sign In' : 'Create Account'} <FiArrowRight /></>}
              </button>
            </form>

            <p className="text-center text-[11px]" style={{ color: '#374151' }}>
              By continuing you agree to our{' '}
              <span className="text-pink-600 cursor-pointer hover:text-pink-400 transition-colors">Terms</span>
              {' & '}
              <span className="text-pink-600 cursor-pointer hover:text-pink-400 transition-colors">Privacy Policy</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}